/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

"use client";

import {
	ArchivedVersionTable,
	ArticleComponentsSection,
	ArticleContainer,
	CosmosComponentContextProps,
	getHero,
	GetLatestCard,
	getSections,
	SectionTypename,
} from "@cosmos/components";
import {
	CosmosEnumOperatingSystems,
	CosmosPluginDownloadDetailFragment,
	CosmosPluginDownloadFragment,
	PagePluginDetailFragment,
} from "@cosmos/hygraph-schema";
import { useAuth } from "@cosmos/next-hooks";
import { useMemo, useState } from "react";

import { css } from "@panda/css";

import { TemplateProviders } from "../templates";

import type { CleanedBlobRes } from "@cosmos/common";

interface CosmosPageProps {
	pageData?: PagePluginDetailFragment;
	componentContext: CosmosComponentContextProps;
	eulaAccepted: boolean;
	eulaEnforcementUrl?: string | null;
	blobs?: CleanedBlobRes[];
}

type GetLatestTopDownloads = {
	downloads?: CosmosPluginDownloadDetailFragment[];
	downloadName?: string;
};

const sectionExtraProps = (__typename: SectionTypename): any => {
	return __typename === "CosmosSectionContentLargeImage" ? { enableAnimate: false } : {};
};

export const CosmosPagePluginDetail = ({
	pageData,
	componentContext,
	eulaEnforcementUrl,
	eulaAccepted,
	blobs,
}: CosmosPageProps): React.JSX.Element => {
	useAuth(!componentContext.enableAuth);

	const [osName, setOsName] = useState<string>();

	const cleanedBlobs = useMemo(() => blobs?.filter((blob) => !!blob?.downloadUrl) || [], [blobs]);
	//organize the blobs by latest version and pluck any blobs that match the version
	const blobDownloads = useMemo(() => {
		if (!cleanedBlobs?.length) return [];

		const first = cleanedBlobs[0];
		const latestSemver = first.semver;
		const result: CosmosPluginDownloadFragment[] = [];

		for (let i = 0; i < cleanedBlobs.length; i++) {
			const blob = cleanedBlobs[i];
			const operatingSystem = (blob.operatingSystem || "windows") as CosmosEnumOperatingSystems;
			const title = blob.name;
			const downloadUrl = blob?.downloadUrl;
			const semver = blob?.semver;

			if (i === 0 || semver !== latestSemver) {
				result.push({
					title,
					details: [
						{
							operatingSystem,
							downloadUrl,
						},
					],
				});
				continue;
			}
		}

		return result;
	}, [cleanedBlobs]);

	const { downloads, lastestDownloads } = useMemo((): {
		downloads: CosmosPluginDownloadFragment[];
		lastestDownloads: GetLatestTopDownloads;
	} => {
		const result = blobDownloads?.length ? blobDownloads : pageData?.downloads || [];
		const lastestDownloads: GetLatestTopDownloads = { downloads: [], downloadName: pageData?.hero?.title?.text || "" };

		for (let i = 0; i < result.length; i++) {
			const item = result[i];
			if (!item.details.length) continue;
			for (const detail of item.details) {
				const operatingSystem = (detail.operatingSystem || "windows") as CosmosEnumOperatingSystems;
				const downloadUrl = detail.downloadUrl;
				if (!downloadUrl) continue;
				if (!lastestDownloads?.downloads) lastestDownloads.downloads = [];
				const osExists = lastestDownloads.downloads?.filter(
					(detail) => detail.operatingSystem === operatingSystem,
				)?.length;
				if (osExists) continue;

				lastestDownloads.downloads.push({
					operatingSystem,
					downloadUrl,
				});
			}
		}

		return { downloads: result, lastestDownloads };
	}, [blobDownloads, pageData?.downloads, pageData?.hero?.title?.text]);

	const overrideDownloadUrl = eulaEnforcementUrl && !eulaAccepted ? eulaEnforcementUrl : null;

	return (
		<TemplateProviders componentContext={componentContext}>
			{getHero(pageData?.hero)}
			<div className={css({ paddingBlockStart: "eds.dimension.32" })}>
				<ArticleContainer>
					<GetLatestCard
						sections={pageData?.latestCardSections}
						downloads={lastestDownloads.downloads}
						downloadName={lastestDownloads.downloadName}
						overrideDownloadUrl={overrideDownloadUrl}
						osName={osName}
						onOsNameChange={setOsName}
					/>
				</ArticleContainer>
				<ArticleComponentsSection articleComponents={pageData?.articleComponents} />
				{pageData?.articleComponents.length && (downloads.length || pageData?.refSections?.length) ? (
					<div className={css({ paddingBlock: "eds.dimension.16" })} />
				) : (
					""
				)}
				<ArticleContainer>
					<ArchivedVersionTable
						items={downloads}
						overrideDownloadUrl={overrideDownloadUrl}
						osName={osName}
						onOsNameChange={setOsName}
					/>
				</ArticleContainer>
				{downloads.length && pageData?.refSections?.length ? (
					<div className={css({ paddingBlock: "eds.dimension.48" })} />
				) : (
					""
				)}
				{getSections(pageData?.refSections, sectionExtraProps)}
				{!pageData?.refSections?.length ? <div className={css({ paddingBlock: "eds.dimension.48" })} /> : ""}
			</div>
		</TemplateProviders>
	);
};
